import { useEffect, useState } from "react";
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Box,
    Grid,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from "@mui/material";

import dayjs from "dayjs";
import "dayjs/locale/es-mx";
import { useDispatch, useSelector } from "react-redux";
import { ExpandMore } from "@mui/icons-material";

import { RootState } from "@store/store";
import { Person } from "@interfaces/persons";
import { filterByNestedCodes } from "@helpers/generals";
import { useLazyGetPepsQuery } from "@services/api/peps";
import { useLazyGetPersonsQuery } from "@services/api/persons";
import { useLazyGetApplicationDetailQuery } from "@services/api/applications";
import { setReloadSection, setStatementsPldReady } from "@store/slices/componentsSlice";

export const DeclaratoriaPld = () => {
    const dispatch = useDispatch();

    const [majorityShareholders, setMajorityShareholders] = useState<Array<Person>>([]);
    const [shareholders, setShareholders] = useState<Array<Person>>([]);

    const applicationSelected = useSelector((state: RootState) => state.components.applicationSelected);
    const reloadSection = useSelector((state: RootState) => state.components.currentSection.reload);
    const printingProfileReport = useSelector((state: RootState) => state.components.printingProfileReport);

    const [triggerGetPersons] = useLazyGetPersonsQuery();

    const [triggerGetPeps, getPepsResponse] = useLazyGetPepsQuery();
    const [triggerGetAppDetail, getAppDetailResponse] = useLazyGetApplicationDetailQuery();

    const reloadInfo = async () => {
        try {
            let params = new URLSearchParams();
            params.append("size", "100");
            params.append("q", `loan_application_id:${applicationSelected.id}`);
            await triggerGetPeps(params.toString(), false).unwrap();

            params = new URLSearchParams([["q", `loan_application_id:${applicationSelected.id}`]]);
            const personsData = await triggerGetPersons(params.toString()).unwrap();

            setMajorityShareholders(
                filterByNestedCodes(personsData, (person) => person.person_types_detail, ["PTAM", "PTSAV"]),
            );

            setShareholders(filterByNestedCodes(personsData, (person) => person.person_types_detail, ["PTPR"]));

            await triggerGetAppDetail(applicationSelected.id ?? "0-0-0-0-0", false).unwrap();

            if (printingProfileReport.printingMode) {
                dispatch(setStatementsPldReady(true));
            }
        } catch (error) {
            console.log(error);
        } finally {
            if (printingProfileReport.printingMode) {
                dispatch(setStatementsPldReady(true));
            }
        }
    };

    useEffect(() => {
        if (reloadSection) {
            reloadInfo().finally(() => dispatch(setReloadSection(false)));
        }
    }, [reloadSection]);

    useEffect(() => {
        if (applicationSelected.id === "0-0-0-0-0" || applicationSelected.id === undefined) return;

        reloadInfo();
    }, [applicationSelected.id]);

    return (
        <Box>
            <Accordion defaultExpanded={printingProfileReport.printingMode}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="body1" color="main3" fontWeight={700} sx={{ opacity: 0.55 }}>
                        Empresa
                    </Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Accordion defaultExpanded={printingProfileReport.printingMode}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography component={"span"} variant="body1" color="main3">
                                ¿Alguno de sus socios, accionistas o propietarios reales, desempeña o ha desempeñado
                                funciones públicas destacadas en México o en el extranjero, altos puestos ejecutivos en
                                empresas estatales o funciones importantes en partidos políticos, en el último año?{" "}
                                <Typography component={"span"} variant="body1" color="main3" fontWeight={700}>
                                    {getAppDetailResponse.currentData?.pep_shareholder === true
                                        ? "Si"
                                        : getAppDetailResponse.currentData?.pep_shareholder === false
                                          ? "No"
                                          : "Sin contestar"}
                                </Typography>
                            </Typography>
                        </AccordionSummary>
                        {getAppDetailResponse.currentData?.pep_shareholder === true && (
                            <AccordionDetails>
                                <TableContainer component={Paper}>
                                    <Table>
                                        <TableHead sx={{ backgroundColor: "#002652" }}>
                                            <TableRow>
                                                <TableCell align="center" sx={{ color: "#FFF" }}>
                                                    Nombre
                                                </TableCell>
                                                <TableCell align="center" sx={{ color: "#FFF" }}>
                                                    Dependencia del cargo
                                                </TableCell>
                                                <TableCell align="center" sx={{ color: "#FFF" }}>
                                                    Cargo público
                                                </TableCell>
                                                <TableCell align="center" sx={{ color: "#FFF" }}>
                                                    ¿En el cargo?
                                                </TableCell>
                                                <TableCell align="center" sx={{ color: "#FFF" }}>
                                                    Fecha de inicio
                                                </TableCell>
                                                <TableCell align="center" sx={{ color: "#FFF" }}>
                                                    Fecha de fin
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {getPepsResponse.currentData
                                                ?.filter(
                                                    (pep) =>
                                                        pep.pep_type.code === "CPEPS" &&
                                                        pep.client_details_id !== null &&
                                                        pep.person_id === null,
                                                )
                                                .map((pep) => (
                                                    <TableRow>
                                                        <TableCell align="center">{pep.name}</TableCell>
                                                        <TableCell align="center">{pep.dependency}</TableCell>
                                                        <TableCell align="center">{pep.position}</TableCell>
                                                        <TableCell align="center">{pep.active ? "Si" : "No"}</TableCell>
                                                        <TableCell align="center">
                                                            {dayjs(pep.start_date)
                                                                .utc(true)
                                                                .local()
                                                                .locale("es-mx")
                                                                .format("YYYY MMM DD")}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {pep.end_date !== null
                                                                ? dayjs(pep.end_date)
                                                                      .utc(true)
                                                                      .local()
                                                                      .locale("es-mx")
                                                                      .format("YYYY MMM DD")
                                                                : ""}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </AccordionDetails>
                        )}
                    </Accordion>

                    <Accordion defaultExpanded={printingProfileReport.printingMode}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography component={"span"} variant="body1" color="main3">
                                ¿El cónyuge o alguno de los padres, abuelos, hijos, nietos, hermanos, suegros, hijos
                                políticos, abuelos políticos o cuñados de los socios, accionistas o propietarios reales,
                                desempeña o ha desempeñado funciones públicas destacadas en México o en el extranjero,
                                altos puestos ejecutivos en empresas estatales o funciones importantes en partidos
                                políticos?{" "}
                                <Typography component={"span"} variant="body1" color="main3" fontWeight={700}>
                                    {getAppDetailResponse.currentData?.pep_members === true
                                        ? "Si"
                                        : getAppDetailResponse.currentData?.pep_members === false
                                          ? "No"
                                          : "Sin contestar"}
                                </Typography>
                            </Typography>
                        </AccordionSummary>
                        {getAppDetailResponse.currentData?.pep_members === true && (
                            <AccordionDetails>
                                <TableContainer component={Paper}>
                                    <Table>
                                        <TableHead sx={{ backgroundColor: "#002652" }}>
                                            <TableRow>
                                                <TableCell align="center" sx={{ color: "#FFF" }}>
                                                    Nombre
                                                </TableCell>
                                                <TableCell align="center" sx={{ color: "#FFF" }}>
                                                    Dependencia del cargo
                                                </TableCell>
                                                <TableCell align="center" sx={{ color: "#FFF" }}>
                                                    Cargo público
                                                </TableCell>
                                                <TableCell align="center" sx={{ color: "#FFF" }}>
                                                    Relación
                                                </TableCell>
                                                <TableCell align="center" sx={{ color: "#FFF" }}>
                                                    ¿En el cargo?
                                                </TableCell>
                                                <TableCell align="center" sx={{ color: "#FFF" }}>
                                                    Fecha de inicio
                                                </TableCell>
                                                <TableCell align="center" sx={{ color: "#FFF" }}>
                                                    Fecha de fin
                                                </TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {getPepsResponse.currentData
                                                ?.filter(
                                                    (pep) =>
                                                        pep.pep_type.code === "CPEPF" &&
                                                        pep.client_details_id !== null &&
                                                        pep.person_id === null,
                                                )
                                                .map((pep) => (
                                                    <TableRow>
                                                        <TableCell align="center">{pep.name}</TableCell>
                                                        <TableCell align="center">{pep.dependency}</TableCell>
                                                        <TableCell align="center">{pep.position}</TableCell>
                                                        <TableCell align="center">
                                                            {pep.relationship_type.name}
                                                        </TableCell>
                                                        <TableCell align="center">{pep.active ? "Si" : "No"}</TableCell>
                                                        <TableCell align="center">
                                                            {dayjs(pep.start_date)
                                                                .utc(true)
                                                                .local()
                                                                .locale("es-mx")
                                                                .format("YYYY MMM DD")}
                                                        </TableCell>
                                                        <TableCell align="center">
                                                            {pep.end_date !== null
                                                                ? dayjs(pep.end_date)
                                                                      .utc(true)
                                                                      .local()
                                                                      .locale("es-mx")
                                                                      .format("YYYY MMM DD")
                                                                : ""}
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </AccordionDetails>
                        )}
                    </Accordion>
                </AccordionDetails>
            </Accordion>
            {applicationSelected.tax_system.tax_system_type === "PM" &&
                majorityShareholders.map((majorityShareholder) => (
                    <Accordion defaultExpanded={printingProfileReport.printingMode} key={majorityShareholder.id}>
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Typography variant="body1" color="main3" fontWeight={700} sx={{ opacity: 0.55 }}>
                                {majorityShareholder.person_types_detail.map((detail) => detail.name).join("/")} -{" "}
                                {majorityShareholder?.name} {majorityShareholder?.last_name}{" "}
                                {majorityShareholder?.last_name_2}
                            </Typography>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Accordion defaultExpanded={printingProfileReport.printingMode}>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Typography component={"span"} variant="body1" color="main3">
                                        ¿Desempeñas o has desempeñado funciones públicas destacadas en México o el
                                        extranjero, altos puestos ejecutivos en empresas estatales o funciones
                                        importantes en partidos políticos?​{" "}
                                        <Typography component={"span"} variant="body1" color="main3" fontWeight={700}>
                                            {majorityShareholder?.pep_shareholder === true
                                                ? "Si"
                                                : majorityShareholder?.pep_shareholder === false
                                                  ? "No"
                                                  : "Sin contestar"}
                                        </Typography>
                                    </Typography>
                                </AccordionSummary>
                                {majorityShareholder?.pep_shareholder === true && (
                                    <AccordionDetails>
                                        <TableContainer component={Paper}>
                                            <Table>
                                                <TableHead sx={{ backgroundColor: "#002652" }}>
                                                    <TableRow>
                                                        <TableCell align="center" sx={{ color: "#FFF" }}>
                                                            Nombre
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ color: "#FFF" }}>
                                                            Dependencia del cargo
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ color: "#FFF" }}>
                                                            Cargo público
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ color: "#FFF" }}>
                                                            ¿En el cargo?
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ color: "#FFF" }}>
                                                            Fecha de inicio
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ color: "#FFF" }}>
                                                            Fecha de fin
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {getPepsResponse.currentData
                                                        ?.filter(
                                                            (pep) =>
                                                                pep.pep_type.code === "CPEPP" &&
                                                                pep.client_details_id === null &&
                                                                pep.person_id === majorityShareholder?.id,
                                                        )
                                                        .map((pep) => (
                                                            <TableRow>
                                                                <TableCell align="center">{pep.name}</TableCell>
                                                                <TableCell align="center">{pep.dependency}</TableCell>
                                                                <TableCell align="center">{pep.position}</TableCell>
                                                                <TableCell align="center">
                                                                    {pep.active ? "Si" : "No"}
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    {dayjs(pep.start_date)
                                                                        .utc(true)
                                                                        .local()
                                                                        .locale("es-mx")
                                                                        .format("YYYY MMM DD")}
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    {pep.end_date !== null
                                                                        ? dayjs(pep.end_date)
                                                                              .utc(true)
                                                                              .local()
                                                                              .locale("es-mx")
                                                                              .format("YYYY MMM DD")
                                                                        : ""}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </AccordionDetails>
                                )}
                            </Accordion>

                            <Accordion defaultExpanded={printingProfileReport.printingMode}>
                                <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Typography component={"span"} variant="body1" color="main3">
                                        ¿Tu cónyuge o alguno de tus padres, abuelos, hijos, nietos, hermanos, suegros,
                                        hijos políticos, abuelos políticos o cuñados, desempeña o ha desempeñado
                                        funciones públicas destacadas en México o el extranjero, altos puestos
                                        ejecutivos en empresas estatales o funciones importantes en partidos políticos?​{" "}
                                        <Typography component={"span"} variant="body1" color="main3" fontWeight={700}>
                                            {majorityShareholder?.pep_members === true
                                                ? "Si"
                                                : majorityShareholder?.pep_members === false
                                                  ? "No"
                                                  : "Sin contestar"}
                                        </Typography>
                                    </Typography>
                                </AccordionSummary>
                                {majorityShareholder?.pep_members === true && (
                                    <AccordionDetails>
                                        <TableContainer component={Paper}>
                                            <Table>
                                                <TableHead sx={{ backgroundColor: "#002652" }}>
                                                    <TableRow>
                                                        <TableCell align="center" sx={{ color: "#FFF" }}>
                                                            Nombre
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ color: "#FFF" }}>
                                                            Dependencia del cargo
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ color: "#FFF" }}>
                                                            Cargo público
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ color: "#FFF" }}>
                                                            Relación
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ color: "#FFF" }}>
                                                            ¿En el cargo?
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ color: "#FFF" }}>
                                                            Fecha de inicio
                                                        </TableCell>
                                                        <TableCell align="center" sx={{ color: "#FFF" }}>
                                                            Fecha de fin
                                                        </TableCell>
                                                    </TableRow>
                                                </TableHead>
                                                <TableBody>
                                                    {getPepsResponse.currentData
                                                        ?.filter(
                                                            (pep) =>
                                                                pep.pep_type.code === "CPEPF" &&
                                                                pep.client_details_id === null &&
                                                                pep.person_id === majorityShareholder?.id,
                                                        )
                                                        .map((pep) => (
                                                            <TableRow>
                                                                <TableCell align="center">{pep.name}</TableCell>
                                                                <TableCell align="center">{pep.dependency}</TableCell>
                                                                <TableCell align="center">{pep.position}</TableCell>
                                                                <TableCell align="center">
                                                                    {pep.relationship_type?.name}
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    {pep.active ? "Si" : "No"}
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    {dayjs(pep.start_date)
                                                                        .utc(true)
                                                                        .local()
                                                                        .locale("es-mx")
                                                                        .format("YYYY MMM DD")}
                                                                </TableCell>
                                                                <TableCell align="center">
                                                                    {pep.end_date !== null
                                                                        ? dayjs(pep.end_date)
                                                                              .utc(true)
                                                                              .local()
                                                                              .locale("es-mx")
                                                                              .format("YYYY MMM DD")
                                                                        : ""}
                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                </TableBody>
                                            </Table>
                                        </TableContainer>
                                    </AccordionDetails>
                                )}
                            </Accordion>
                        </AccordionDetails>
                    </Accordion>
                ))}

            {shareholders.map((shareholder, idx) => (
                <Accordion defaultExpanded={printingProfileReport.printingMode}>
                    <AccordionSummary expandIcon={<ExpandMore />}>
                        <Typography variant="body1" color="main3" fontWeight={700} sx={{ opacity: 0.55 }}>
                            Propietario Real {idx + 1} - {shareholder.name} {shareholder.last_name}{" "}
                            {shareholder.last_name_2}
                        </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                        <Accordion defaultExpanded={printingProfileReport.printingMode}>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography component={"span"} variant="body1" color="main3">
                                    ¿Desempeñas o has desempeñado funciones públicas destacadas en México o el
                                    extranjero, altos puestos ejecutivos en empresas estatales o funciones importantes
                                    en partidos políticos?​{" "}
                                    <Typography component={"span"} variant="body1" color="main3" fontWeight={700}>
                                        {shareholder?.pep_shareholder === true
                                            ? "Si"
                                            : shareholder?.pep_shareholder === false
                                              ? "No"
                                              : "Sin contestar"}
                                    </Typography>
                                </Typography>
                            </AccordionSummary>
                            {shareholder?.pep_shareholder === true && (
                                <AccordionDetails>
                                    <TableContainer component={Paper}>
                                        <Table>
                                            <TableHead sx={{ backgroundColor: "#002652" }}>
                                                <TableRow>
                                                    <TableCell align="center" sx={{ color: "#FFF" }}>
                                                        Nombre
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ color: "#FFF" }}>
                                                        Dependencia del cargo
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ color: "#FFF" }}>
                                                        Cargo público
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ color: "#FFF" }}>
                                                        ¿En el cargo?
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ color: "#FFF" }}>
                                                        Fecha de inicio
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ color: "#FFF" }}>
                                                        Fecha de fin
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {getPepsResponse.currentData
                                                    ?.filter(
                                                        (pep) =>
                                                            pep.pep_type.code === "CPEPP" &&
                                                            pep.client_details_id === null &&
                                                            pep.person_id === shareholder?.id,
                                                    )
                                                    .map((pep) => (
                                                        <TableRow>
                                                            <TableCell align="center">{pep.name}</TableCell>
                                                            <TableCell align="center">{pep.dependency}</TableCell>
                                                            <TableCell align="center">{pep.position}</TableCell>
                                                            <TableCell align="center">
                                                                {pep.active ? "Si" : "No"}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {dayjs(pep.start_date)
                                                                    .utc(true)
                                                                    .local()
                                                                    .locale("es-mx")
                                                                    .format("YYYY MMM DD")}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {pep.end_date !== null
                                                                    ? dayjs(pep.end_date)
                                                                          .utc(true)
                                                                          .local()
                                                                          .locale("es-mx")
                                                                          .format("YYYY MMM DD")
                                                                    : ""}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </AccordionDetails>
                            )}
                        </Accordion>

                        <Accordion defaultExpanded={printingProfileReport.printingMode}>
                            <AccordionSummary expandIcon={<ExpandMore />}>
                                <Typography component={"span"} variant="body1" color="main3">
                                    ¿Tu cónyuge o alguno de tus padres, abuelos, hijos, nietos, hermanos, suegros, hijos
                                    políticos, abuelos políticos o cuñados, desempeña o ha desempeñado funciones
                                    públicas destacadas en México o el extranjero, altos puestos ejecutivos en empresas
                                    estatales o funciones importantes en partidos políticos?{" "}
                                    <Typography component={"span"} variant="body1" color="main3" fontWeight={700}>
                                        {shareholder?.pep_members === true
                                            ? "Si"
                                            : shareholder?.pep_members === false
                                              ? "No"
                                              : "Sin contestar"}
                                    </Typography>
                                </Typography>
                            </AccordionSummary>
                            {shareholder?.pep_members === true && (
                                <AccordionDetails>
                                    <TableContainer component={Paper}>
                                        <Table>
                                            <TableHead sx={{ backgroundColor: "#002652" }}>
                                                <TableRow>
                                                    <TableCell align="center" sx={{ color: "#FFF" }}>
                                                        Nombre
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ color: "#FFF" }}>
                                                        Dependencia del cargo
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ color: "#FFF" }}>
                                                        Cargo público
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ color: "#FFF" }}>
                                                        Relación
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ color: "#FFF" }}>
                                                        ¿En el cargo?
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ color: "#FFF" }}>
                                                        Fecha de inicio
                                                    </TableCell>
                                                    <TableCell align="center" sx={{ color: "#FFF" }}>
                                                        Fecha de fin
                                                    </TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {getPepsResponse.currentData
                                                    ?.filter(
                                                        (pep) =>
                                                            pep.pep_type.code === "CPEPF" &&
                                                            pep.client_details_id === null &&
                                                            pep.person_id === shareholder?.id,
                                                    )
                                                    .map((pep) => (
                                                        <TableRow>
                                                            <TableCell align="center">{pep?.name}</TableCell>
                                                            <TableCell align="center">{pep?.dependency}</TableCell>
                                                            <TableCell align="center">{pep?.position}</TableCell>
                                                            <TableCell align="center">
                                                                {pep?.relationship_type?.name}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {pep?.active ? "Si" : "No"}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {dayjs(pep.start_date)
                                                                    .utc(true)
                                                                    .local()
                                                                    .locale("es-mx")
                                                                    .format("YYYY MMM DD")}
                                                            </TableCell>
                                                            <TableCell align="center">
                                                                {pep.end_date !== null
                                                                    ? dayjs(pep.end_date)
                                                                          .utc(true)
                                                                          .local()
                                                                          .locale("es-mx")
                                                                          .format("YYYY MMM DD")
                                                                    : ""}
                                                            </TableCell>
                                                        </TableRow>
                                                    ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>
                                </AccordionDetails>
                            )}
                        </Accordion>
                    </AccordionDetails>
                </Accordion>
            ))}
        </Box>
    );
};
